import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { COMMUNITY_LINKS } from '../../utils/constants';
import { messagingAPI } from '../../services/messagingAPI';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

const BOT_LINK = 'https://t.me/GHDM_Notifications_bot';

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

interface TelegramModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TelegramModal: React.FC<TelegramModalProps> = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useAuth();
  const [linkCode, setLinkCode] = useState('');
  const [loadingLink, setLoadingLink] = useState(false);

  // Guest phone linking state
  const [guestPhone, setGuestPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [loadingPhoneCode, setLoadingPhoneCode] = useState(false);
  const [phoneCodeGenerated, setPhoneCodeGenerated] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset guest state on close
      setGuestPhone('');
      setPhoneCode('');
      setPhoneCodeGenerated(false);
      return;
    }
    if (!isAuthenticated || linkCode) return;
    setLoadingLink(true);
    messagingAPI.generateTelegramLink()
      .then(res => { if (res?.data?.code) setLinkCode(res.data.code); })
      .catch(() => {})
      .finally(() => setLoadingLink(false));
  }, [isOpen, isAuthenticated, linkCode]);

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  }, []);

  const handleGeneratePhoneCode = useCallback(async () => {
    if (!guestPhone || guestPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setLoadingPhoneCode(true);
    try {
      const res = await api.post('/messaging/telegram/generate-phone-link', {
        phone: guestPhone
      });
      if (res.data.success) {
        setPhoneCode(res.data.data.code);
        setPhoneCodeGenerated(true);
        toast.success('Code generated! Send it to the bot.');
      } else {
        toast.error(res.data.message || 'Failed to generate code');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to generate code');
    } finally {
      setLoadingPhoneCode(false);
    }
  }, [guestPhone]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            onClick={e => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#229ED9] to-[#1a8bbf] px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white flex-shrink-0">
                  <TelegramIcon />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Get Order Alerts on Telegram</h3>
                  <p className="text-xs text-blue-100">Receive order, payment & system alerts</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-all shrink-0" aria-label="Close modal">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-4">
              {/* Step 1 */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
                <p className="text-xs font-bold text-blue-800 dark:text-blue-200 uppercase tracking-wide mb-1">Step 1 — Subscribe to alerts</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Tap the button below, then press <strong>Start</strong> in the bot.
                </p>
                <a
                  href={BOT_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#229ED9] hover:bg-[#1a8bbf] text-white font-semibold rounded-xl transition-colors text-sm"
                >
                  <TelegramIcon />
                  Open Bot &amp; Subscribe
                </a>
              </div>

              {/* Step 2 — Account linking (authenticated users only) */}
              {isAuthenticated && (
                <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                    Step 2 — Link your account <span className="text-gray-400 font-normal normal-case">(optional)</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    For <strong>personalized</strong> alerts tied to your orders and payments.
                  </p>
                  {loadingLink ? (
                    <div className="text-center py-2 text-xs text-gray-400">Generating code…</div>
                  ) : linkCode ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <code className="flex-1 text-sm font-bold text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 tracking-widest">
                          /link {linkCode}
                        </code>
                        <button
                          onClick={() => handleCopy(`/link ${linkCode}`)}
                          className="text-xs px-2.5 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors font-medium flex-shrink-0"
                        >
                          Copy
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500">Expires in 10 minutes</p>
                    </>
                  ) : (
                    <p className="text-xs text-gray-400">
                      Could not generate code —{' '}
                      <button onClick={() => { setLinkCode(''); }} className="text-blue-500 hover:underline">try again</button>
                    </p>
                  )}
                </div>
              )}

              {/* Step 3 — Phone linking (guests / storefront customers) */}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl p-4">
                <p className="text-xs font-bold text-emerald-800 dark:text-emerald-200 uppercase tracking-wide mb-1">
                  {isAuthenticated ? 'Or' : 'Step 2'} — Link by phone <span className="font-normal normal-case">(guests)</span>
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Link your <strong>recipient phone number</strong> to receive delivery alerts for your orders.
                </p>

                {!phoneCodeGenerated ? (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="tel"
                        value={guestPhone}
                        onChange={e => setGuestPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="0534788219"
                        className="flex-1 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        maxLength={10}
                      />
                      <button
                        onClick={handleGeneratePhoneCode}
                        disabled={loadingPhoneCode || guestPhone.length < 10}
                        className="text-xs px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {loadingPhoneCode ? '...' : 'Get Code'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Must match the phone used in a recent order (within 48 hours)
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <code className="flex-1 text-sm font-bold text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 tracking-widest">
                        /verify {phoneCode}
                      </code>
                      <button
                        onClick={() => handleCopy(`/verify ${phoneCode}`)}
                        className="text-xs px-2.5 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors font-medium flex-shrink-0"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
                      <CheckCircleIcon className="h-3.5 w-3.5" />
                      <span>Send this code to the bot to complete linking</span>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Expires in 10 minutes</p>
                  </>
                )}
              </div>

              <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                Also{' '}
                <a href={COMMUNITY_LINKS.TELEGRAM} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                  join our community channel
                </a>
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TelegramModal;
